import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { ChevronLeft, MapPin, Loader2 } from 'lucide-react';
import {
  Heading,
  Text,
  Stack,
} from '@/components/ui/index.js';
import styles from './LocationStep.module.css';

/**
 * LocationStep - Location input step
 * 
 * Features:
 * - Location input field with neon focus
 * - Use current location button
 * - Skip option
 * - Geolocation loading state
 */
export function LocationStep({
  location,
  onLocationChange,
  onContinue,
  onBack,
}) {
  const [isLocating, setIsLocating] = useState(false);
  const [error, setError] = useState(null);

  const handleUseCurrentLocation = () => {
    setIsLocating(true);
    setError(null);

    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      setIsLocating(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        // In a real app, you would reverse geocode this
        // For now, we'll just show a generic location
        onLocationChange('Current Location');
        setIsLocating(false);
      },
      (err) => {
        setError('Unable to retrieve your location');
        setIsLocating(false);
      },
      { timeout: 10000 }
    );
  };

  const handleInputChange = (e) => {
    onLocationChange(e.target.value);
    if (error) setError(null);
  };

  const handleSkip = () => {
    onContinue();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onContinue();
  };

  return (
    <Stack direction="column" gap={6} fullHeight>
      {/* Header */}
      <Stack direction="column" gap={3}>
        <button
          type="button"
          onClick={onBack}
          className={styles.backButton}
          aria-label="Go back"
        >
          <ChevronLeft size={20} />
          <span>Back</span>
        </button>

        <Stack direction="column" gap={2}>
          <Heading level={2} size="lg" color="primary" align="left">
            Where do you play?
          </Heading>
          <Text size="base" color="secondary" align="left">
            Help us find activities and venues near you.
          </Text>
        </Stack>
      </Stack>

      {/* Location Form */}
      <form onSubmit={handleSubmit} className={styles.form}>
        <Stack direction="column" gap={4}>
          {/* Location Input */}
          <div className={styles.inputWrapper}>
            <label htmlFor="location" className={styles.label}>
              <MapPin size={16} />
              <span>City or zip code</span>
            </label>
            <input
              id="location"
              type="text"
              value={location}
              onChange={handleInputChange}
              placeholder="Enter your location..."
              className={styles.input}
              autoFocus
            />
            {error && (
              <span className={styles.error}>
                {error}
              </span>
            )}
          </div>

          {/* Use Current Location Button */}
          <button
            type="button"
            onClick={handleUseCurrentLocation}
            disabled={isLocating}
            className={styles.locationButton}
          >
            {isLocating ? (
              <>
                <Loader2 size={18} className={styles.spinner} />
                <span>Getting location...</span>
              </>
            ) : (
              <>
                <MapPin size={18} />
                <span>Use current location</span>
              </>
            )}
          </button>
        </Stack>
      </form>

      {/* Footer */}
      <div className={styles.footer}>
        <button
          type="button"
          onClick={onContinue}
          className={styles.continueButton}
        >
          Continue
        </button>
        
        <button
          type="button"
          onClick={handleSkip}
          className={styles.skipButton}
        >
          Skip for now
        </button>
      </div>
    </Stack>
  );
}

LocationStep.propTypes = {
  location: PropTypes.string.isRequired,
  onLocationChange: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

export default LocationStep;
